/* eslint-disable */
import axios from "axios";
import store from "@/store";
import customModal from "../customModal/customModal.vue";

export default {
  name: "ViewProgramModal",
  components: { customModal },

  props: {
    programId: {
      type: String,
      required: true,
    },
    tocMapId: {
      type: String,
      default: "",
    },
  },

  emits: ["closeModal"],

  data() {
    return {
      loading: false,
      programData: null,   // from GET /programme/getprogrambyid/:id  (primary)
      registryData: null,  // from GET /programme/program/registry?TocMapId=... (secondary, non-blocking)
      activeTab: "rprogram",
      copied: false,
      baseUrl: process.env.VUE_APP_Service_URL,
      lastFetchedId: "",
      registryFetched: false,
      viewFetched: false,
      viewData: null,
    };
  },

  watch: {
    programId: {
      immediate: true,
      async handler(newId) {
        if (!newId || newId === this.lastFetchedId) return;
        this.lastFetchedId = newId;
        this.resetState();
        await this.fetchAll(newId);
        if (this.activeTab === "rprogram") {
          this.viewFetched = true;
          this.fetchProgramView(newId).catch(() => {});
        }
      },
    },
    activeTab(newTab) {
      if (newTab === "trace" && !this.registryFetched && this.programId) {
        this.registryFetched = true;
        this.fetchProgramRegistry(this.programId).catch(() => {});
      }
      if (newTab === "rprogram" && !this.viewFetched && this.programId) {
        this.viewFetched = true;
        this.fetchProgramView(this.programId).catch(() => {});
      }
    },
  },

  computed: {
    program() {
      if (!this.programData) return null;

      const p = this.programData;
      let r = this.registryData; // may be null
      let v = this.viewData;     // may be null

      // If the API wrapped the response in { success, count, programs: [...] }
      if (r && r.programs && Array.isArray(r.programs) && r.programs.length > 0) r = r.programs[0];
      if (v && v.programs && Array.isArray(v.programs) && v.programs.length > 0) v = v.programs[0];

      // Safe getter for properties that might be flat or nested under a specific category
      const getVal = (obj, category, key1, key2) => {
        if (!obj) return null;
        if (obj[category]) {
          if (obj[category][key1] !== undefined) return obj[category][key1];
          if (key2 && obj[category][key2] !== undefined) return obj[category][key2];
        }
        if (obj[key1] !== undefined) return obj[key1];
        if (key2 && obj[key2] !== undefined) return obj[key2];
        return null;
      };

      // Return first element when API returns arrays for fields
      const firstVal = (v) => {
        if (v === undefined || v === null) return null;
        if (Array.isArray(v)) return v.length ? v[0] : null;
        return v;
      };

      const badgeMap = {
        Validated:              "badge-success",
        "In Development":       "badge-dev",
        "Ready For Validation": "badge-ai",
        "No Program":           "badge-dev",
        Active:                 "badge-success",
      };
      const statusBadgeClass = badgeMap[p.Status] || "badge-dev";

      const programConfig = {
        "Program Name":    p.ProgramName    || "N/A",
        "Program Title":   p.ProgramTitle   || "N/A",
        "Report Type":     p.ReportType     || "N/A",
        "Chart Type":      p.ChartType      || "—",
        "Return Type":     p.ReturnType     || "N/A",
        "Orientation":     p.Orientation    || "N/A",
        "Program Type":    p.ProgramType    || "N/A",
        "Version":         p.Version || p.version || "1.0",
        "Interim Version": p.InterimVersion || "Base",
      };

      const dataFilters = {
        "Dataset Location":   p.Location            || "N/A",
        "Location Type":      p.LocationType        || "N/A",
        "Column Filters":     p.ColumnFilters?.length ? p.ColumnFilters.join(", ") : "N/A",
        "Group By":           p.GroupBy             || "—",
        "Unique Field":       p.UniqueField         || "—",
        "Include Total":      p.IncludeTotal        ? "Yes" : "No",
        "Numeric Operations": p.NumericOperations?.length ? p.NumericOperations.join(", ") : "—",
        "Page Group":         p.PageGroup           || "—",
        "Foot Note":          p.FootNote || p.Footnote || "—",
      };

      const assignment = {
        "Study":         p.StudyRef || p.StudyName       || "N/A",
        "Author":        p.Author   || p.DeveloperName   || "N/A",
        "Developer":     p.DeveloperName                 || "—",
        "QC Programmer": p.QCName                        || "—",
        "Status":        { label: p.Status || "In Development", badgeClass: statusBadgeClass },
        "Created By":    p.CreatedByEmail || p.CreatedBy || "—",
        "Created At":    p.CreatedAt     ? p.CreatedAt.replace("T", " ").substring(0, 19)        : "—",
        "Last Modified": p.LastModifiedAt ? p.LastModifiedAt.replace("T", " ").substring(0, 19) : "—",
      };

      let rowFilters = {};
      if (p.RowFilters?.length) {
        p.RowFilters.forEach((filter, idx) => {
          const suffix = p.RowFilters.length > 1 ? ` (${idx + 1})` : "";
          rowFilters[`Field${suffix}`]    = filter.ColumnName || "—";
          rowFilters[`Operator${suffix}`] = filter.Operator   || "—";
          rowFilters[`Value${suffix}`]    = filter.Value !== undefined ? filter.Value : "—";
        });
      } else {
        rowFilters = { Field: "—", Operator: "—", Value: "—" };
      }

      // R Program code — use real script if view/registry returned it, else annotated mock
      const getScript = (obj) => {
        if (!obj) return null;
        const s = getVal(obj, "ProgramRegistry", "ScriptContent", "scriptContent") || obj.Script || obj.script;
        if (Array.isArray(s)) return s.length > 0 ? s[0] : null;
        return s || null;
      };

      const rawScript = getScript(v) || getScript(r) || getScript(p) || null;

      let rProgramCodeLines = [];
      if (rawScript) {
        rProgramCodeLines = rawScript.split("\n").map((raw) => {
          if (raw.trimStart().startsWith("#")) {
            return { tokens: [{ cls: "code-comment", text: raw }] };
          }
          const tokens = [];
          const kwRe = /\b(if|else|for|while|function|return|TRUE|FALSE|NULL|NA|library|require|stop)\b/g;
          let last = 0, m;
          while ((m = kwRe.exec(raw)) !== null) {
            if (m.index > last) tokens.push({ cls: "code-plain",   text: raw.slice(last, m.index) });
            tokens.push({ cls: "code-keyword", text: m[0] });
            last = m.index + m[0].length;
          }
          if (last < raw.length) tokens.push({ cls: "code-plain", text: raw.slice(last) });
          return { tokens: tokens.length ? tokens : [{ cls: "code-plain", text: raw }] };
        });
      } else {
        const progName = p.ProgramName    || "stats.R";
        const tocId    = p.TocId          || this.tocMapId || "—";
        const title    = p.ProgramTitle   || "Demographics Summary";
        const repType  = p.ReportType     || "GRAPH";
        const ver      = String(p.ProgramVersion || p.Version || "1.0").replace(/^v/i, "");
        const author   = p.Author         || p.DeveloperName || "N/A";

        rProgramCodeLines = [
          { tokens: [{ cls: "code-comment", text: "# =============================================================" }] },
          { tokens: [{ cls: "code-comment", text: `# Program  : ${progName}` }] },
          { tokens: [{ cls: "code-comment", text: `# ToC ID   : ${tocId}` }] },
          { tokens: [{ cls: "code-comment", text: `# Title    : ${title}` }] },
          { tokens: [{ cls: "code-comment", text: `# Type     : ${repType}` }] },
          { tokens: [{ cls: "code-comment", text: `# Version  : v${ver}  (Auto-generated)` }] },
          { tokens: [{ cls: "code-comment", text: `# Author   : ${author}` }] },
          { tokens: [{ cls: "code-comment", text: "# =============================================================" }] },
          { tokens: [] },
          { tokens: [{ cls: "code-comment", text: "# --- 1. PARAMS -----------------------------------------------" }] },
          { tokens: [{ cls: "code-plain", text: "args   <- " }, { cls: "code-func", text: "commandArgs" }, { cls: "code-plain", text: "(trailingOnly = " }, { cls: "code-keyword", text: "TRUE" }, { cls: "code-plain", text: ")" }] },
          { tokens: [{ cls: "code-keyword", text: "if" }, { cls: "code-plain", text: " (" }, { cls: "code-func", text: "length" }, { cls: "code-plain", text: "(args) < 1) " }, { cls: "code-func", text: "stop" }, { cls: "code-plain", text: "(" }, { cls: "code-string", text: '"Params JSON path required"' }, { cls: "code-plain", text: ")" }] },
          { tokens: [{ cls: "code-plain", text: "params <- jsonlite::" }, { cls: "code-func", text: "fromJSON" }, { cls: "code-plain", text: "(args[1])" }] },
          { tokens: [] },
          { tokens: [{ cls: "code-plain", text: "program_name   <- params$program_name" }] },
          { tokens: [{ cls: "code-plain", text: "run_id         <- params$run_id" }] },
          { tokens: [{ cls: "code-plain", text: "output_path    <- params$output_path" }] },
          { tokens: [{ cls: "code-plain", text: "column_filters <- " }, { cls: "code-func", text: "as.character" }, { cls: "code-plain", text: "(" }, { cls: "code-func", text: "unlist" }, { cls: "code-plain", text: "(params$column_filters))" }] },
          { tokens: [{ cls: "code-plain", text: "chart_type     <- params$chart_type" }] },
          { tokens: [] },
          { tokens: [{ cls: "code-comment", text: "# --- 2. LIBRARIES -----------------------------------------------" }] },
          { tokens: [{ cls: "code-func", text: "library" }, { cls: "code-plain", text: "(dplyr)" }] },
          { tokens: [{ cls: "code-func", text: "library" }, { cls: "code-plain", text: "(ggplot2)" }] },
          { tokens: [{ cls: "code-func", text: "(base64enc)" }] },
          { tokens: [{ cls: "code-func", text: "library" }, { cls: "code-plain", text: "(jsonlite)" }] },
          { tokens: [] },
          { tokens: [{ cls: "code-comment", text: "# --- 3. LOAD DATA -----------------------------------------------" }] },
          { tokens: [{ cls: "code-plain", text: "df <- " }, { cls: "code-func", text: "read.csv" }, { cls: "code-plain", text: "(params$data_csv_path, stringsAsFactors = " }, { cls: "code-keyword", text: "FALSE" }, { cls: "code-plain", text: ")" }] },
          { tokens: [{ cls: "code-keyword", text: "if" }, { cls: "code-plain", text: " (" }, { cls: "code-func", text: "nrow" }, { cls: "code-plain", text: "(df) == 0) " }, { cls: "code-func", text: "stop" }, { cls: "code-plain", text: "(" }, { cls: "code-string", text: '"No data in input CSV"' }, { cls: "code-plain", text: ")" }] },
        ];
      }

      // Execution log
      let executionLog = [];
      const runs = getVal(v, "ExecutionLog", "Runs", "runs") || getVal(r, "ExecutionLog", "Runs", "runs") || [];
      
      const tcExecVal = getVal(v || r, "ExecutionLog", "ExecutionCount", "executionCount") || (p.TraceabilityChain && p.TraceabilityChain.ExecutionCount);
      const tcExecCount = (tcExecVal !== undefined && tcExecVal !== null) ? tcExecVal : 0;
      const tcLastExecutedAt = getVal(v || r, "ExecutionLog", "LastExecutedAt", "lastExecutedAt") || (p.TraceabilityChain && p.TraceabilityChain.LastExecutedAt) || null;
      const tcContentHash = getVal(v || r, "ProgramRegistry", "ContentHash", "contentHash") || (p.TraceabilityChain && p.TraceabilityChain.ContentHash) || "—";

      if (Array.isArray(runs) && runs.length) {
        executionLog = runs.map((run) => ({
          runId:           run.RunId || run._id || "—",
          runAt:           run.RunAt ? run.RunAt.replace("T", " ").substring(0, 16) : "—",
          runBy:           run.RunBy || (run.RunByEmail ? run.RunByEmail.split("@")[0] : "system"),
          status:          run.Status || "Success",
          statusBadgeClass:badgeMap[run.Status] || "badge-success",
          contentHash:     run.ContentHash || run.contentHash || tcContentHash,
        }));
      } else {
        if (tcLastExecutedAt) {
          executionLog.push({
            runId:           `run-${tcExecCount}`,
            runAt:           tcLastExecutedAt.replace("T", " ").substring(0, 16),
            runBy:           p.LastModifiedByEmail ? p.LastModifiedByEmail.split("@")[0] : "system",
            status:          "Success", statusBadgeClass: "badge-success",
            contentHash:     tcContentHash,
          });
        }
        if (Number(tcExecCount) > 1 && p.CreatedAt) {
          executionLog.push({
            runId:           "run-1",
            runAt:           p.CreatedAt.replace("T", " ").substring(0, 16),
            runBy:           p.CreatedByEmail ? p.CreatedByEmail.split("@")[0] : "system",
            status:          "Success", statusBadgeClass: "badge-success",
            contentHash:     tcContentHash,
          });
        }
        if (!executionLog.length) {
          if (p.OutputDate) {
            executionLog.push({
              runId: "run-latest",
              runAt: p.OutputDate.replace("T", " ").substring(0, 16),
              runBy: p.LastModifiedByEmail ? p.LastModifiedByEmail.split("@")[0] : "system",
              status: "Success", statusBadgeClass: "badge-success",
              contentHash: tcContentHash,
            });
          }
          if (p.CreatedAt) {
            executionLog.push({
              runId: "run-initial",
              runAt: p.CreatedAt.replace("T", " ").substring(0, 16),
              runBy: p.CreatedByEmail ? p.CreatedByEmail.split("@")[0] : "system",
              status: "Success", statusBadgeClass: "badge-success",
              contentHash: tcContentHash,
            });
          }
        }
        if (!executionLog.length) {
          executionLog.push({ runId: "—", runAt: "—", runBy: "—", status: "—", statusBadgeClass: "badge-dev", contentHash: "—" });
        }
      }

      // Traceability nodes
      const traceSource = {
        _id:         firstVal(getVal(v || r, "TocProgrammeMapItem", "TocMapId", "tocMapId")) || p._id || this.programId || "—",
        TocId:       firstVal(getVal(v || r, "TocProgrammeMapItem", "TocId", "tocId"))       || p.TocId || this.tocMapId  || "—",
        Study:       firstVal(getVal(v || r, "TocProgrammeMapItem", "Study", "study"))       || p.Study   || p.StudyRef || p.StudyName || "—",
        ReportId:    firstVal(getVal(v || r, "TocProgrammeMapItem", "ReportId", "reportId")) || p.ReportId    || "—",
        ProgramName: firstVal(getVal(v || r, "TocProgrammeMapItem", "ProgramName", "programName")) || p.ProgramName || "—",
      };

      const traceRegistry = r || v
        ? {
            registryId:   getVal(r || v, "ProgramRegistry", "RegistryId", "registryId") || "—",
            scriptFile:   getVal(r || v, "ProgramRegistry", "ScriptFile", "scriptFile") || p.ProgramName || "—",
            scriptPath:   getVal(r || v, "ProgramRegistry", "ScriptPath", "scriptPath") || `scripts/${p.ProgramName || "—"}`,
            contentHash:  tcContentHash,
            registeredBy: getVal(r || v, "ProgramRegistry", "RegisteredBy", "registeredBy") || getVal(r || v, "ProgramRegistry", "CreatedByEmail", "createdByEmail") || "—",
            registeredAt: (() => {
              const raw = getVal(r || v, "ProgramRegistry", "RegisteredAt", "registeredAt") || getVal(r || v, "ProgramRegistry", "CreatedAt", "createdAt");
              return raw ? raw.replace("T", " ").substring(0, 19) : "—";
            })(),
            status: {
              label:      getVal(r || v, "ProgramRegistry", "Status", "status") || "Active",
              badgeClass: badgeMap[getVal(r || v, "ProgramRegistry", "Status", "status")] || "badge-success",
            },
          }
        : {
            registryId: "—", scriptFile: p.ProgramName || "—",
            scriptPath: `scripts/${p.ProgramName || "—"}`,
            registeredBy: "—", registeredAt: "—",
            status: { label: "Pending", badgeClass: "badge-dev" },
          };

      const bStorage = (r && r.AzureBlobStorage) || (v && v.AzureBlobStorage) || (r && r.BlobStorage) || (v && v.BlobStorage);

      const toArray = (val) => {
        if (Array.isArray(val)) return val;
        if (val === undefined || val === null) return [];
        if (typeof val === "string") {
          try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed)) return parsed;
          } catch (e) {
            // not JSON
          }
          return [val];
        }
        return [String(val)];
      };

      const traceBlobStorage = bStorage
        ? (() => {
            const container = bStorage.Container || bStorage.container || "StatPrograms/scripts/";
            const file = bStorage.File || bStorage.file || bStorage.FileName || p.ProgramName || "—";
            const localBackup = bStorage.LocalBackup || bStorage.localBackup || bStorage.Localbackup || `/app/scripts/${p.ProgramName || "—"}`;

            const fileArr = toArray(bStorage.File || bStorage.file || bStorage.FileName || p.ProgramName);
            const localBackupArr = toArray(bStorage.LocalBackup || bStorage.localBackup || bStorage.Localbackup || `/app/scripts/${p.ProgramName || "—"}`);
            const commitArr = toArray(bStorage.CommitID || bStorage.CommitId || bStorage.Commit || bStorage.commitId || bStorage.CommitIds);
            const coreBranchArr = toArray(bStorage.CoreBranch || bStorage.coreBranch || bStorage.Core_Branch || bStorage.corebranch);
            const branchArr = toArray(bStorage.Branch || bStorage.WorkingBranch || bStorage.branch || bStorage.workingBranch || bStorage.Working_Branch);

            return {
              container,
              file,
              File: fileArr,
              localBackup,
              LocalBackup: localBackupArr,
              CommitID: commitArr,
              CoreBranch: coreBranchArr,
              Branch: branchArr,
              CommitIDDisplay: commitArr.length ? commitArr.join(", ") : "—",
              CoreBranchDisplay: coreBranchArr.length ? coreBranchArr.join(", ") : "—",
              BranchDisplay: branchArr.length ? branchArr.join(", ") : "—",
            };
          })()
        : {
            container: "StatPrograms/scripts/",
            file: p.ProgramName || "—",
            File: [p.ProgramName || "—"],
            localBackup: `/app/scripts/${p.ProgramName || "—"}`,
            LocalBackup: [`/app/scripts/${p.ProgramName || "—"}`],
            CommitID: [], CoreBranch: [], Branch: [],
            CommitIDDisplay: "—", CoreBranchDisplay: "—", BranchDisplay: "—",
          };

      return {
        programTitle:    p.ProgramTitle || p.ProgramName || "N/A",
        programFile:     p.ProgramName  || "N/A",
        reportType:      p.ReportType   || "N/A",
        version:         p.Version || p.Version || "1.0",
        author:          p.Author || p.DeveloperName   || "N/A",
        status:          p.Status || "In Development",
        statusBadgeClass,
        programConfig, dataFilters, assignment, rowFilters,
        rProgram: {
          fileName:    p.ProgramName || "stats.R",
          version:     `v${String(p.ProgramVersion || p.Version || "1.0").replace(/^v/i, "")}`,
          generatedBy: p.ProgramType ? `${p.ProgramType} Program` : "Auto-generated",
          contentHash: getVal(v || r, "ProgramRegistry", "ContentHash", "contentHash") || "—",
          codeLines:   rProgramCodeLines,
        },
        traceability: {
          runCount: Number(tcExecCount) || executionLog.length,
          source: traceSource, registry: traceRegistry,
          blobStorage: traceBlobStorage, executionLog,
        },
      };
    },
  },

  methods: {
    resetState() {
      this.programData  = null;
      this.registryData = null;
      this.viewData     = null;
      this.activeTab    = "rprogram";
      this.registryFetched = false;
      this.viewFetched  = false;
    },

    async fetchAll(programId) {
      this.loading = true;
      try {
        // PRIMARY — must succeed; loading gate is tied only to this call
        await this.fetchProgramById(programId);
      } finally {
        // Unblock UI immediately after primary call resolves or fails
        this.loading = false;
      }
    },

    /** Primary: confirmed working endpoint */
    async fetchProgramById(programId) {
      const idtoken = store.getters.getIdToken;
      try {
        const res = await axios.get(
          `${this.baseUrl}/stats-python/programme/getprogrambyid/${programId}`,
          {
            headers: {
              Authorization:  "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          }
        );
        this.programData = res.data;
      } catch (err) {
        console.error("fetchProgramById failed:", err);
        this.programData = null;
      }
    },

    unwrapRData(obj) {
      if (Array.isArray(obj)) {
         if (obj.length === 1 && obj[0] !== null && typeof obj[0] !== "object") {
           return obj[0];
         }
         return obj.map((o) => this.unwrapRData(o));
      } else if (obj !== null && typeof obj === "object") {
         if (Object.keys(obj).length === 0) return null;
         const newObj = {};
         for (const key in obj) {
           newObj[key] = this.unwrapRData(obj[key]);
         }
         return newObj;
      }
      return obj;
    },

    /**
     * GET /stats-r/program/registry
     * Non-blocking — enriches traceability tab with live registry data when available.
     */
    async fetchProgramRegistry(programId) {
      const idtoken = store.getters.getIdToken;

      const params = {
        TocMapId: this.tocMapId || this.programId,
      };

      try {
        const res = await axios.get(
          `${this.baseUrl}/stats-r/program/registry`,
          {
            params,
            timeout: 10000,
            headers: {
              Authorization:  "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          }
        );

        const data = this.unwrapRData(res.data);
        
        if (Array.isArray(data) && data.length > 0) {
          this.registryData =
            data.find((d) => d.TocMapId === this.tocMapId || d.tocMapId === this.tocMapId) ||
            data.find((d) => d.ProgramId === programId    || d.programId === programId)    ||
            data[0];
        } else if (data && typeof data === "object" && Object.keys(data).length > 0) {
          this.registryData = data;
        } else {
          this.registryData = null;
        }
      } catch (err) {
        if (err.response && err.response.status !== 404) {
          console.warn("fetchProgramRegistry failed:", err.message);
        }
        this.registryData = null;
      }
    },

    /**
     * GET /stats-r/program/view
     * Non-blocking — retrieves code and run history from program view service.
     */
    async fetchProgramView(programId) {
      const idtoken = store.getters.getIdToken;

      const params = {
        TocMapId: this.tocMapId || this.programId,
      };

      try {
        const res = await axios.get(
          `${this.baseUrl}/stats-r/program/view`,
          {
            params,
            timeout: 10000,
            headers: {
              Authorization:  "Bearer " + idtoken,
              "Content-Type": "application/json",
            },
          }
        );

        const data = this.unwrapRData(res.data);
        
        if (Array.isArray(data) && data.length > 0) {
          this.viewData =
            data.find((d) => d.TocMapId === this.tocMapId || d.tocMapId === this.tocMapId) ||
            data.find((d) => d.ProgramId === programId    || d.programId === programId)    ||
            data[0];
        } else if (data && typeof data === "object" && Object.keys(data).length > 0) {
          this.viewData = data;
        } else {
          this.viewData = null;
        }
      } catch (err) {
        if (err.response && err.response.status !== 404) {
          console.warn("fetchProgramView failed:", err.message);
        }
        this.viewData = null;
      }
    },

    getRawCodeText() {
      if (!this.program || !this.program.rProgram || !this.program.rProgram.codeLines) return "";
      return this.program.rProgram.codeLines.map(line => 
        line.tokens.map(t => t.text).join("")
      ).join("\n");
    },

    copyCode() {
      const text = this.getRawCodeText();
      navigator.clipboard.writeText(text).catch(() => {});
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 1500);
    },

    downloadCode() {
      const text = this.getRawCodeText();
      if (!text) return;
      const blob = new Blob([text], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = (this.program?.rProgram?.fileName || "program.R").replace(/\.[^/.]+$/, "")+".R"
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },

    closeModal() {
      this.$emit("closeModal");
    },
  },
};